import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/layout';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Trophy, Star, Users, Send, X, Award, Check, Mail, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const AWARDS = [
  { id: 'peoples_choice', name: "People's Choice", icon: Trophy, color: 'text-yellow-500', description: 'Most popular councillor' },
  { id: 'most_authentic', name: 'Most Authentic', icon: Star, color: 'text-purple-500', description: 'Genuine and transparent' },
  { id: 'community_champion', name: 'Community Champion', icon: Users, color: 'text-green-500', description: 'Best for the community' },
];

const CouncilTracker = () => {
  const [councillors, setCouncillors] = useState([]);
  const [votes, setVotes] = useState([]);
  const [awardVotes, setAwardVotes] = useState({});
  const [userVotes, setUserVotes] = useState({});
  const [councillorStats, setCouncillorStats] = useState({});
  const [selectedCouncillor, setSelectedCouncillor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showVoteModal, setShowVoteModal] = useState(false);
  const [selectedAward, setSelectedAward] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [councillorsRes, votesRes] = await Promise.all([
        api.get('/councillors/with-stats'),
        api.get('/votes'),
      ]);
      
      const councillorsData = councillorsRes.data;
      setCouncillors(councillorsData);
      setVotes(votesRes.data);

      // Extract stats from councillors data (now included in response)
      const statsMap = {};
      for (const c of councillorsData) {
        if (c.stats) {
          statsMap[c.id] = c.stats;
        }
      }
      setCouncillorStats(statsMap);

      // Fetch award votes
      try {
        const awardRes = await api.get('/councillor-awards');
        setAwardVotes(awardRes.data.totals || {});
        if (user) {
          setUserVotes(awardRes.data.user_votes || {});
        }
      } catch (e) {
        console.log('Award votes not available yet');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load council data');
    } finally {
      setLoading(false);
    }
  };

  const handleCouncillorClick = (councillor) => {
    setSelectedCouncillor(councillor);
  };

  const handleVoteForAward = async (awardId) => {
    if (!user) {
      toast.error('Please log in to vote');
      return;
    }

    if (!selectedCouncillor) {
      toast.error('Please select a councillor first');
      return;
    }

    // Check if already voted for this award
    if (userVotes[awardId]) {
      toast.error(`You've already voted for ${AWARDS.find(a => a.id === awardId)?.name} this year`);
      return;
    }

    try {
      await api.post('/councillor-awards/vote', {
        councillor_id: selectedCouncillor.id,
        award_type: awardId,
      });
      
      toast.success(`Vote cast for ${selectedCouncillor.name}!`);
      setUserVotes({ ...userVotes, [awardId]: selectedCouncillor.id });
      setShowVoteModal(false);
      fetchData(); // Refresh vote counts
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to vote';
      toast.error(msg);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please log in to send a message');
      return;
    }

    if (!message.trim()) {
      toast.error('Message cannot be empty');
      return;
    }

    setSending(true);

    try {
      await api.post('/messages', {
        councillor_id: selectedCouncillor.id,
        message: message,
      });

      toast.success('Message sent successfully!');
      setMessage('');
      setShowMessageModal(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send message';
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const getVoteStatus = (vote) => {
    if (!selectedCouncillor) return null;
    if (vote.yes_votes.includes(selectedCouncillor.match_name)) return 'YES';
    if (vote.no_votes.includes(selectedCouncillor.match_name)) return 'NO';
    if (vote.abstained_votes?.includes(selectedCouncillor.match_name)) return 'ABSTAINED';
    if (vote.not_present_votes?.includes(selectedCouncillor.match_name)) return 'NOT PRESENT';
    if (vote.conflict_votes?.includes(selectedCouncillor.match_name)) return 'CONFLICT';
    return 'ABSENT';
  };

  const getAwardCount = (councillorId, awardId) => {
    return awardVotes[councillorId]?.[awardId] || 0;
  };

  const filteredVotes = selectedCouncillor
    ? votes.filter(
        (vote) =>
          vote.yes_votes.includes(selectedCouncillor.match_name) ||
          vote.no_votes.includes(selectedCouncillor.match_name)
      )
    : votes;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A52A] mx-auto"></div>
            <p className="text-gray-500 mt-4 font-mono text-sm">Loading council data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="text-center mb-8">
          <h1 className="font-headline text-4xl md:text-5xl text-white mb-2">
            COUNCIL TRACKER
          </h1>
          <p className="font-mono text-xs text-gray-500 tracking-[0.2em] uppercase">
            Regina City Council
          </p>
        </div>

        {/* Awards Legend */}
        <div className="mb-8 bg-[#111] border border-[#1a1a1a] rounded p-4">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-[#C5A52A]" />
            <span className="font-display text-sm text-white tracking-wider">ANNUAL AWARDS</span>
            <span className="font-mono text-[10px] text-gray-500">(1 VOTE PER CATEGORY PER YEAR)</span>
          </div>
          <div className="flex flex-wrap gap-4">
            {AWARDS.map((award) => (
              <div key={award.id} className="flex items-center gap-2">
                <award.icon className={`w-4 h-4 ${award.color}`} />
                <span className="font-mono text-xs text-gray-400">{award.name}</span>
                {userVotes[award.id] && (
                  <span className="flex items-center gap-1 bg-green-900/30 rounded px-1">
                    <Check className="w-3 h-3 text-green-500" />
                    <span className="font-mono text-[10px] text-green-500">VOTED</span>
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Councillors Grid */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-gray-400 mb-6 uppercase tracking-widest font-mono flex items-center gap-2">
            🛡️ Select a Councillor
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {councillors.map((c) => (
              <div
                key={c.id}
                onClick={() => handleCouncillorClick(c.id === selectedCouncillor?.id ? null : c)}
                className={
                  'cursor-pointer border bg-gray-900 p-4 rounded-sm transition-all duration-200 group relative overflow-hidden ' +
                  (selectedCouncillor?.id === c.id
                    ? 'border-white bg-[#1a1a1a] shadow-[0_0_15px_rgba(255,255,255,0.1)]'
                    : 'border-gray-800 hover:border-gray-600')
                }
                data-testid={`councillor-${c.id}`}
              >
                {selectedCouncillor?.id === c.id && (
                  <div className="absolute top-2 right-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full shadow-[0_0_8px_#00ff41]"></div>
                  </div>
                )}

                {/* Award badges */}
                <div className="absolute top-2 left-2 flex gap-1">
                  {getAwardCount(c.id, 'peoples_choice') > 0 && (
                    <div className="bg-yellow-900/50 rounded p-0.5" title={`${getAwardCount(c.id, 'peoples_choice')} votes`}>
                      <Trophy className="w-3 h-3 text-yellow-500" />
                    </div>
                  )}
                  {getAwardCount(c.id, 'most_authentic') > 0 && (
                    <div className="bg-purple-900/50 rounded p-0.5" title={`${getAwardCount(c.id, 'most_authentic')} votes`}>
                      <Star className="w-3 h-3 text-purple-500" />
                    </div>
                  )}
                  {getAwardCount(c.id, 'community_champion') > 0 && (
                    <div className="bg-green-900/50 rounded p-0.5" title={`${getAwardCount(c.id, 'community_champion')} votes`}>
                      <Users className="w-3 h-3 text-green-500" />
                    </div>
                  )}
                </div>

                <div className="h-20 w-20 mx-auto rounded-full bg-gray-800 mb-3 overflow-hidden border-2 border-gray-700 group-hover:border-gray-500 transition-colors">
                  <img
                    src={c.image_url}
                    alt={c.name}
                    className={
                      'object-cover w-full h-full transition-all duration-500 ' +
                      (selectedCouncillor?.id === c.id
                        ? 'grayscale-0'
                        : 'grayscale group-hover:grayscale-0')
                    }
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        c.name
                      )}&background=000&color=fff`;
                    }}
                  />
                </div>

                <div className="text-center">
                  <div
                    className={
                      'font-bold text-sm leading-tight mb-1 ' +
                      (selectedCouncillor?.id === c.id
                        ? 'text-white'
                        : 'text-gray-400 group-hover:text-green-500')
                    }
                  >
                    {c.name}
                  </div>
                  <div className="text-[10px] text-gray-600 font-mono uppercase tracking-widest">
                    {c.ward}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Selected Councillor Profile */}
        {selectedCouncillor && (
          <div className="mb-12 border border-gray-700 bg-[#0a0a0a] rounded-sm p-6 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-green-500"></div>
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-white uppercase tracking-wider font-mono">
                    Profile: <span className="text-green-500">{selectedCouncillor.name}</span>
                  </h3>
                  <button
                    onClick={() => setSelectedCouncillor(null)}
                    className="text-gray-500 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-gray-400 mb-4 max-w-3xl leading-relaxed text-sm">
                  {selectedCouncillor.bio}
                </p>

                {/* Response Stats */}
                {councillorStats[selectedCouncillor.id] && (
                  <div className="bg-gray-900 border border-gray-800 rounded p-4 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className="w-4 h-4 text-[#C5A52A]" />
                      <span className="font-mono text-xs text-[#C5A52A] tracking-wider uppercase">MESSAGE RESPONSIVENESS</span>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="text-center">
                        <div className="font-mono text-2xl text-white font-bold">
                          {councillorStats[selectedCouncillor.id].total_received}
                        </div>
                        <div className="text-[10px] uppercase text-gray-500 font-mono">Received</div>
                      </div>
                      <div className="text-center">
                        <div className="font-mono text-2xl text-green-500 font-bold">
                          {councillorStats[selectedCouncillor.id].total_responded}
                        </div>
                        <div className="text-[10px] uppercase text-gray-500 font-mono">Responded</div>
                      </div>
                      <div className="text-center">
                        <div className="font-mono text-2xl text-yellow-500 font-bold">
                          {councillorStats[selectedCouncillor.id].total_redeemed}
                        </div>
                        <div className="text-[10px] uppercase text-gray-500 font-mono" title="Responded outside 7-day window">Redeemed*</div>
                      </div>
                      <div className="text-center">
                        <div className={`font-mono text-2xl font-bold ${
                          councillorStats[selectedCouncillor.id].responsiveness_pct >= 70 
                            ? 'text-green-500' 
                            : councillorStats[selectedCouncillor.id].responsiveness_pct >= 40 
                              ? 'text-yellow-500' 
                              : 'text-red-500'
                        }`}>
                          {councillorStats[selectedCouncillor.id].responsiveness_pct}%
                        </div>
                        <div className="text-[10px] uppercase text-gray-500 font-mono">Rate</div>
                      </div>
                    </div>
                    <p className="mt-2 text-[10px] text-gray-600 font-mono">
                      *Redeemed = responded outside 7-day window (doesn't affect rate)
                    </p>
                  </div>
                )}

                {/* Award Vote Counts */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {AWARDS.map((award) => (
                    <div 
                      key={award.id} 
                      className="bg-gray-900 p-4 border border-gray-800 text-center"
                    >
                      <award.icon className={`w-6 h-6 ${award.color} mx-auto mb-2`} />
                      <div className="font-mono text-2xl text-white font-bold">
                        {getAwardCount(selectedCouncillor.id, award.id)}
                      </div>
                      <div className="text-[10px] uppercase text-gray-500 font-mono">
                        {award.name}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setShowMessageModal(true)}
                    className="flex items-center gap-2 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-bold py-3 px-6 rounded-sm uppercase tracking-wider transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    Send Anonymous Message
                  </button>
                  
                  {user && (
                    <button
                      onClick={() => setShowVoteModal(true)}
                      className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-sm uppercase tracking-wider transition-colors"
                    >
                      <Award className="w-4 h-4" />
                      Vote for Award
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Voting Records */}
        <div>
          <div className="flex justify-between items-end mb-6 border-b border-gray-800 pb-2">
            <h2 className="text-xl font-bold text-white uppercase tracking-widest font-mono flex items-center gap-2">
              📄
              {selectedCouncillor
                ? `VOTING RECORD: ${selectedCouncillor.name.toUpperCase()}`
                : 'ALL RECORDED VOTES'}
            </h2>
            {selectedCouncillor && (
              <button
                onClick={() => setSelectedCouncillor(null)}
                className="text-xs text-red-500 font-mono hover:text-white hover:underline uppercase tracking-wider"
              >
                [RESET FILTER]
              </button>
            )}
          </div>

          <div className="space-y-1">
            {filteredVotes.map((vote) => (
              <div
                key={vote.id}
                className="grid grid-cols-12 gap-4 border-b p-4 items-center transition-colors bg-gray-900 border-gray-800 hover:bg-gray-800"
              >
                <div className="col-span-12 md:col-span-2 font-mono text-xs text-gray-500">
                  {vote.date}
                </div>

                <div className="col-span-12 md:col-span-8">
                  <div className="text-lg font-bold text-gray-200 mb-2 leading-tight">
                    {vote.motion}
                  </div>

                  {!selectedCouncillor ? (
                    <div className="text-xs text-gray-500 font-mono flex gap-4">
                      <span className="flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        {vote.yes_votes.length} YES
                      </span>
                      <span className="flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        {vote.no_votes.length} NO
                      </span>
                    </div>
                  ) : (
                    <div className="mt-1 flex items-center gap-6 font-mono text-sm">
                      <div className="flex items-center gap-2 border-l-2 pl-3 border-gray-700">
                        <span className="text-gray-500 text-xs uppercase tracking-wider">
                          {selectedCouncillor.name.split(' ').pop()} voted:
                        </span>
                        {getVoteStatus(vote) === 'YES' && <span className="text-green-500 font-black tracking-widest">YES</span>}
                        {getVoteStatus(vote) === 'NO' && <span className="text-red-500 font-black tracking-widest">NO</span>}
                        {getVoteStatus(vote) === 'ABSTAINED' && <span className="text-yellow-500 font-bold tracking-widest">ABSTAINED</span>}
                        {getVoteStatus(vote) === 'NOT PRESENT' && <span className="text-gray-500 font-bold tracking-widest">NOT PRESENT</span>}
                        {getVoteStatus(vote) === 'CONFLICT' && <span className="text-orange-500 font-bold tracking-widest">CONFLICT</span>}
                        {getVoteStatus(vote) === 'ABSENT' && <span className="text-gray-500 font-bold tracking-widest">ABSENT</span>}
                        {!['YES','NO','ABSTAINED','NOT PRESENT','CONFLICT','ABSENT'].includes(getVoteStatus(vote)) && (
                          <span className="text-gray-600 text-xs">NOT RECORDED</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 flex gap-3">
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                          {vote.yes_votes.length} yes
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                          {vote.no_votes.length} no
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-span-12 md:col-span-2 text-right flex justify-end items-center md:block">
                  {vote.source === 'ai' && (
                    <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded bg-purple-900/40 border border-purple-700/50 text-purple-400 mb-1" title="Extracted by AI Auditor">
                      AI
                    </span>
                  )}
                  <span
                    className={
                      'inline-block text-xs font-bold px-3 py-1 rounded border font-mono whitespace-nowrap ' +
                      (vote.result.includes('CARRIED')
                        ? 'border-green-900 text-green-500 bg-green-900/10'
                        : 'border-red-900 text-red-500 bg-red-900/10')
                    }
                  >
                    {vote.result}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Message Modal */}
      {showMessageModal && selectedCouncillor && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 border border-gray-800 w-full max-w-lg rounded-sm">
            <div className="p-6 border-b border-gray-800 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white uppercase font-mono">
                Message {selectedCouncillor.name}
              </h2>
              <button
                onClick={() => setShowMessageModal(false)}
                className="text-gray-500 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSendMessage} className="p-6 space-y-4">
              <div className="bg-yellow-900/20 border border-yellow-900/50 p-4 rounded-sm">
                <p className="text-yellow-500 text-sm font-mono">
                  ⚠️ Your message will be sent anonymously. The councillor will not see your identity.
                </p>
              </div>

              <div>
                <label className="block text-sm font-mono text-gray-400 mb-2 uppercase tracking-wider">
                  Your Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows="6"
                  className="w-full px-4 py-3 bg-gray-950 border border-gray-700 text-white focus:border-green-500 focus:outline-none rounded-sm resize-none"
                  placeholder="Share your concerns or questions..."
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowMessageModal(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-sm uppercase tracking-wider transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="flex-1 bg-[#C5A52A] hover:bg-[#D4B93A] text-black font-bold py-3 px-4 rounded-sm uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? 'Sending...' : 'Send Anonymously'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vote Modal */}
      {showVoteModal && selectedCouncillor && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 border border-gray-800 w-full max-w-lg rounded-sm">
            <div className="p-6 border-b border-gray-800 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white uppercase font-mono">
                Vote for {selectedCouncillor.name}
              </h2>
              <button
                onClick={() => setShowVoteModal(false)}
                className="text-gray-500 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-900/20 border border-blue-900/50 p-4 rounded-sm">
                <p className="text-blue-400 text-sm font-mono">
                  🗳️ You get ONE vote per award category per year. Votes reset January 1st.
                </p>
              </div>

              <div className="space-y-3">
                {AWARDS.map((award) => (
                  <button
                    key={award.id}
                    onClick={() => handleVoteForAward(award.id)}
                    disabled={userVotes[award.id]}
                    className={`w-full p-4 rounded border transition-all flex items-center gap-4 ${
                      userVotes[award.id]
                        ? 'bg-gray-800 border-gray-700 opacity-50 cursor-not-allowed'
                        : 'bg-gray-950 border-gray-700 hover:border-gray-500 cursor-pointer'
                    }`}
                  >
                    <div className={`p-3 rounded bg-gray-800 ${award.color}`}>
                      <award.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-bold text-white">{award.name}</div>
                      <div className="text-xs text-gray-500">{award.description}</div>
                    </div>
                    {userVotes[award.id] ? (
                      <span className="flex items-center gap-1 bg-green-900/30 rounded px-2 py-1">
                        <Check className="w-4 h-4 text-green-500" />
                        <span className="font-mono text-xs text-green-500">VOTED</span>
                      </span>
                    ) : (
                      <span className="font-mono text-xs text-[#C5A52A]">CAST VOTE →</span>
                    )}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setShowVoteModal(false)}
                className="w-full bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 px-4 rounded-sm uppercase tracking-wider transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CouncilTracker;
